import { ApiError, retryAfterSecondsOf } from "./apiFetch";
import type { PortalCaregiver } from "../types/api";

/** The server's send cooldown, the fallback when a 429 carries no `retryAfterSeconds`. */
const COOLDOWN_SECONDS = 600;

/** The 422 detail prefix for a caregiver phone equal to the patient's phone or home phone. */
const PHONE_COLLISION_DETAIL = "caregiver_phone duplicates";

export type CaregiverState = "confirmed" | "pending" | "expired";

/**
 * Whole minutes until `inviteExpiresAt`, rounded up and never below 1 while
 * the invitation is live; 0 once it has passed. `now` is a `Date.now()` value.
 */
export function inviteMinutesLeft(inviteExpiresAt: string, now: number): number {
  const remaining = Date.parse(inviteExpiresAt) - now;
  return remaining <= 0 ? 0 : Math.max(1, Math.ceil(remaining / 60_000));
}

/**
 * The display state of the one caregiver: `inviteExpiresAt` against `now`
 * splits the pending row. The server stores no expired state; the member and
 * admin surfaces both derive it here.
 */
export function caregiverState(caregiver: PortalCaregiver, now: number): CaregiverState {
  if (caregiver.status === "confirmed") return "confirmed";
  return inviteMinutesLeft(caregiver.inviteExpiresAt, now) === 0 ? "expired" : "pending";
}

/**
 * The in-place message for a 429 send cooldown (the add, edit and resend
 * share it), or null for any other error. The wait is the 429's
 * `retryAfterSeconds` in whole minutes, rounded up and never below 1.
 */
export function cooldownMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 429) return null;
  const seconds = retryAfterSecondsOf(err) ?? COOLDOWN_SECONDS;
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return `You can send another invitation in ${minutes} ${minutes === 1 ? "minute" : "minutes"}.`;
}

/** Whether `err` is the 422 for a caregiver phone equal to the patient's phone or home phone. */
export function isCaregiverPhoneCollision(err: unknown): boolean {
  return (
    err instanceof ApiError && err.status === 422 && err.detail.startsWith(PHONE_COLLISION_DETAIL)
  );
}
